import { FC, useCallback, useEffect } from 'react';
import { Controller, ResolverOptions, useForm } from 'react-hook-form';
import { useFetcher, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { z } from 'zod';
import { PatientIdField } from '@samd/features/orders/components/patient_id_form_field';
import { IntegerInput } from 'src/components/integer_input';
import { SelectList } from 'src/components/select_list';
import {
  GleasonScoreFields,
  REQUIRED_GLEASON_SCORE_FIELDS_SCHEMA,
} from 'src/distribution_platforms/features/components/gleason_score_form_fields';
import {
  emptyToUndefined,
  FormActions,
  FormFieldProps,
  processFetcherResponse,
  getProstateBiopsyTStageOptions,
  PROSTATE_BIOPSY_T_STAGES,
  safeEmptyToUndefined,
  safeParseInt,
  submitOrderAction,
} from 'src/distribution_platforms/features/order_form';
import {
  SamdOrderDetailFieldsFragment,
  OrderStatus,
  SpecimenType,
  TStage,
} from 'src/types';
import { OrderAction } from 'src/types/loaders';
import { notifyError, notifySuccess, SpecimenTypeUtils } from 'src/utils';

const FORM_CONSTANTS = {
  WIDTH: '840px',
  MIN_AGE: 55,
  MAX_AGE: 98,
  MAX_PSA: 100,
  PSA_MIN: 0.01,
} as const;

export type UsProstateBiopsyFormData = z.infer<
  typeof UsProstateBiopsyFormSchema
>;

export const UsProstateBiopsyFormSchema = z.object({
  externalId: z.string().min(1, 'Patient ID is required'),
  patientAge: z
    .number({
      error: (issue) => {
        if (issue.code === 'invalid_type') {
          return { message: 'Patient age is required' };
        }

        return null;
      },
    })
    .min(
      FORM_CONSTANTS.MIN_AGE,
      `Acceptable age range for patient is from ${FORM_CONSTANTS.MIN_AGE} to ${FORM_CONSTANTS.MAX_AGE}`,
    )
    .max(
      FORM_CONSTANTS.MAX_AGE,
      `Acceptable age range for patient is from ${FORM_CONSTANTS.MIN_AGE} to ${FORM_CONSTANTS.MAX_AGE}`,
    )
    .nullable()
    .superRefine((val, ctx) => {
      if (val === null) {
        ctx.addIssue({ code: 'custom', message: 'Patient age is required' });
      }
    }),
  ...REQUIRED_GLEASON_SCORE_FIELDS_SCHEMA.shape,
  tStage: z.enum(PROSTATE_BIOPSY_T_STAGES).optional(),
});

// Custom resolver
const createCustomResolver =
  () =>
  async (
    data: UsProstateBiopsyFormData,
    context: unknown,
    options: ResolverOptions<UsProstateBiopsyFormData>,
  ) => {
    return zodResolver(UsProstateBiopsyFormSchema)(data, context, options);
  };

// Helper function to create default form values
const createDefaultFormValues = (
  initialOrder: SamdOrderDetailFieldsFragment,
): UsProstateBiopsyFormData => ({
  externalId: initialOrder.externalId?.toString() ?? '',
  patientAge: initialOrder.patientAge ?? null,
  primaryGleasonScore: (initialOrder.primaryGleasonScore?.toString() ?? '') as
    | '3'
    | '4'
    | '5',
  secondaryGleasonScore: (initialOrder.secondaryGleasonScore?.toString() ??
    '') as '3' | '4' | '5',
  tStage: initialOrder.tStage || undefined,
});

const PatientAgeField: FC<FormFieldProps<UsProstateBiopsyFormData>> = ({
  control,
  errors,
  trigger,
}) => {
  return (
    <Controller
      name='patientAge'
      control={control}
      render={({ field }) => (
        <IntegerInput
          required
          label='Age at Diagnosis'
          data-testid='patientAge'
          {...field}
          error={!!errors.patientAge}
          helperText={errors.patientAge?.message?.toString() ?? ' '}
          min={FORM_CONSTANTS.MIN_AGE}
          max={FORM_CONSTANTS.MAX_AGE}
          onChange={(value) => {
            field.onChange(value);
            trigger?.('patientAge');
          }}
        />
      )}
    />
  );
};

const TStageField: FC<FormFieldProps<UsProstateBiopsyFormData>> = ({
  control,
  errors,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      gap: { xs: 2, md: 3 },
    }}
  >
    <Controller
      name='tStage'
      control={control}
      render={({ field }) => (
        <SelectList
          labelId='t-stage-label'
          data-testid='t-stage'
          label='T-stage'
          {...field}
          options={getProstateBiopsyTStageOptions(false, false)}
          validationText={errors.tStage?.message?.toString() ?? ''}
          onChange={field.onChange}
        />
      )}
    />
  </Box>
);

export const UsSamdProstateBiopsyOrderForm: FC<{
  initialOrder: SamdOrderDetailFieldsFragment;
  onClose: () => void;
}> = ({ initialOrder, onClose }) => {
  const theme = useTheme();
  const fetcher = useFetcher();
  const location = useLocation();
  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
  } = useForm<UsProstateBiopsyFormData>({
    resolver: createCustomResolver(),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: createDefaultFormValues(initialOrder),
  });

  // Convert form data to Order object
  const getOrderFromFormData =
    useCallback((): SamdOrderDetailFieldsFragment => {
      const formData = getValues();

      return {
        ...initialOrder,
        externalId: emptyToUndefined(formData.externalId) ?? null,
        patientAge: formData.patientAge,
        primaryGleasonScore: safeParseInt(formData.primaryGleasonScore),
        secondaryGleasonScore: safeParseInt(formData.secondaryGleasonScore),
        tStage: safeEmptyToUndefined(formData.tStage) as TStage | undefined,
        specimenType: SpecimenType.ProstateBiopsy,
        status: initialOrder.status,
      } as SamdOrderDetailFieldsFragment;
    }, [getValues, initialOrder]);

  // Trigger validation for edit forms to show validation errors immediately
  useEffect(() => {
    if (initialOrder.id) {
      trigger();
    }
  }, [initialOrder, trigger]);

  // Handle action results
  useEffect(() => {
    const result = processFetcherResponse(fetcher.data, fetcher.state);

    if (result) {
      if (result.type === 'success') {
        notifySuccess(result.message);
        if (result.closeDialog) {
          onClose();
        }
      } else if (result.type === 'error') {
        notifyError(result.message);
      }
    }
  }, [fetcher.data, fetcher.state, onClose]);

  const isNewOrder = !initialOrder.id;
  const isDraftOrder = initialOrder.status === OrderStatus.Draft;

  const handleSaveOrder = async () => {
    const orderData = getOrderFromFormData();

    if (!orderData.id) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.CREATE,
        location,
        orderData,
      );
    } else {
      submitOrderAction(
        fetcher.submit,
        OrderAction.UPDATE,
        location,
        orderData,
      );
    }
  };

  const handleSubmitOrder = async () => {
    const orderData = getOrderFromFormData();

    // Decide between submit and resubmit based on order status
    // Use RESUBMIT for orders that have been submitted before (not Draft status)
    const action =
      orderData.status === OrderStatus.Draft
        ? OrderAction.SUBMIT
        : OrderAction.RESUBMIT;

    submitOrderAction(fetcher.submit, action, location, orderData);
  };

  const handleDiscard = async () => {
    // Unsaved orders do not need to be deleted because they don't exist yet
    if (isDraftOrder && !isNewOrder) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.DELETE,
        location,
        undefined,
      );
    } else {
      notifySuccess('Order discarded successfully.');
      onClose();
    }
  };

  const formTitle = `${isNewOrder ? 'Create' : 'Edit'} ${SpecimenTypeUtils.getSpecimenTypeName(SpecimenType.ProstateBiopsy)} Test Order`;

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(handleSubmitOrder)}
      noValidate
      sx={{
        width: '100%',
        maxWidth: FORM_CONSTANTS.WIDTH,
        px: { xs: 0, sm: 2, md: 0 },
      }}
    >
      <Typography
        variant='h5'
        sx={{
          display: 'block',
          fontWeight: theme.typography.fontWeightBold,

          fontSize: {
            xs: theme.typography.h6.fontSize,
            sm: theme.typography.h5.fontSize,
          },
        }}
      >
        {formTitle}
      </Typography>
      <Typography
        variant='caption'
        sx={{
          display: 'block',
          color: 'text.secondary',
          mb: 2,
        }}
      >
        *Indicates a required field
      </Typography>
      <Typography
        variant='caption'
        sx={{
          display: 'block',
          color: 'text.secondary',
          mb: 1,
        }}
      >
        Protected Health Information, such as patient name, date of birth, or
        medical record number, should not be entered into this web portal.
      </Typography>
      <Stack spacing={{ xs: 2, sm: 2 }}>
        <PatientIdField
          control={control}
          errors={errors}
          onChange={async (_) => {
            await trigger('externalId');
          }}
        />
        <PatientAgeField control={control} errors={errors} trigger={trigger} />
        <GleasonScoreFields
          control={control}
          errors={errors}
          trigger={trigger}
        />
        <TStageField control={control} errors={errors} />
        <FormActions
          showSubmitButton={true}
          showSaveButton={false}
          showDraftActionButtons={isDraftOrder || isNewOrder}
          onValidateBeforeSubmit={async () => trigger()}
          onConfirmSubmit={handleSubmitOrder}
          onSaveAsDraft={handleSaveOrder}
          onDiscard={handleDiscard}
          requireDiscardConfirmation={!isNewOrder}
          onDiscardSuccess={() => {
            notifySuccess('Order discarded successfully.');
            onClose();
          }}
        />
      </Stack>
    </Box>
  );
};
